import { useTheme, type ThemeMode } from "../hooks/useTheme";
import { isPushSupported, isPushEnabled, enablePush, disablePush } from "../lib/push";
import { invoke } from "../lib/bridge";
import { useState, useEffect, useCallback } from "react";
import { Spinner } from "./ui/Spinner";
import { Alert } from "./ui/Alert";

interface SelfMemberInfo {
  isSelf: boolean;
  avatarUrl: string | null;
  avatarEncrypted: boolean;
}

interface BackfillResult {
  ok: boolean;
  reason?: "no_avatar" | "already_encrypted";
}

export function MemberSettings() {
  const { mode, setMode } = useTheme();
  const [pushSupported] = useState(() => isPushSupported());
  const [notifyOn, setNotifyOn] = useState(false);
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [retention, setRetention] = useState<number | null>(null);
  const [retentionInput, setRetentionInput] = useState("");
  const [retentionSaving, setRetentionSaving] = useState(false);
  const [retentionError, setRetentionError] = useState<string | null>(null);

  // Avatar encryption backfill (task 65): surface a visible, user-initiated
  // "Encrypt now" affordance for legacy plaintext avatars (task 63/64) instead
  // of migrating silently.
  const [selfMember, setSelfMember] = useState<SelfMemberInfo | null>(null);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillError, setBackfillError] = useState<string | null>(null);

  const refreshSelfMember = useCallback(async () => {
    try {
      const members = await invoke<SelfMemberInfo[]>("list_members");
      const self = Array.isArray(members) ? members.find((m) => m.isSelf) : undefined;
      setSelfMember(self ?? null);
    } catch (e) {
      console.error("Failed to load member data:", e);
    }
  }, []);

  useEffect(() => {
    refreshSelfMember();
  }, [refreshSelfMember]);

  const handleEncryptNow = async () => {
    if (backfillBusy) return;
    setBackfillBusy(true);
    setBackfillError(null);
    try {
      const result = await invoke<BackfillResult>("backfill_own_avatar");
      if (result.ok) {
        await refreshSelfMember();
      } else {
        setBackfillError("Couldn't encrypt your photo. Try again.");
      }
    } catch (e) {
      console.error("Failed to backfill avatar encryption:", e);
      setBackfillError("Couldn't encrypt your photo. Try again.");
    }
    setBackfillBusy(false);
  };

  const showEncryptBanner = !!selfMember?.avatarUrl && selfMember?.avatarEncrypted === false;

  useEffect(() => {
    if (pushSupported) isPushEnabled().then(setNotifyOn).catch(() => {});
  }, [pushSupported]);

  useEffect(() => {
    invoke<number>("get_chat_retention")
      .then((d) => { setRetention(d); setRetentionInput(String(d)); })
      .catch((e) => console.error("Failed to load chat retention:", e));
  }, []);

  const handleSaveRetention = async () => {
    const days = Number(retentionInput);
    if (!Number.isInteger(days) || days <= 0) {
      setRetentionError("Enter a whole number greater than 0.");
      return;
    }
    if (days === retention) return;
    setRetentionSaving(true);
    setRetentionError(null);
    try {
      const saved = await invoke<number>("set_chat_retention", { days });
      setRetention(saved);
      setRetentionInput(String(saved));
    } catch (e) {
      console.error("Failed to update chat retention:", e);
      setRetentionError("Couldn't save. Try again.");
    }
    setRetentionSaving(false);
  };

  const handleToggleNotify = async (enable: boolean) => {
    if (notifyBusy || enable === notifyOn) return;
    setNotifyBusy(true);
    try {
      if (enable) {
        setNotifyOn(await enablePush());
      } else {
        await disablePush();
        setNotifyOn(false);
      }
    } catch (e) {
      console.error("Failed to toggle notifications:", e);
    }
    setNotifyBusy(false);
  };

  return (
    <>
      {showEncryptBanner && (
        <div className="member-detail-row">
          <p className="member-detail-hint" style={{ margin: "0 0 8px" }}>
            Your profile photo isn't encrypted yet.
          </p>
          {backfillError && <Alert>{backfillError}</Alert>}
          <button
            onClick={handleEncryptNow}
            disabled={backfillBusy}
            className="btn-secondary btn-small"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            {backfillBusy && <Spinner size={14} />}
            {backfillBusy ? "Encrypting..." : "Encrypt now"}
          </button>
        </div>
      )}

      <div className="member-detail-row">
        <span className="field-label">Dark Mode</span>
        <div className="theme-toggle">
          {(["light", "system", "dark"] as ThemeMode[]).map((opt) => (
            <button
              key={opt}
              className={`theme-toggle-btn ${mode === opt ? "active" : ""}`}
              onClick={() => setMode(opt)}
            >
              {opt === "light" ? "Off" : opt === "dark" ? "On" : "System"}
            </button>
          ))}
        </div>
      </div>

      {pushSupported && (
        <div className="member-detail-row">
          <span className="field-label">Notifications</span>
          <div className="theme-toggle">
            <button
              className={`theme-toggle-btn ${!notifyOn ? "active" : ""}`}
              onClick={() => handleToggleNotify(false)}
              disabled={notifyBusy}
            >
              Off
            </button>
            <button
              className={`theme-toggle-btn ${notifyOn ? "active" : ""}`}
              onClick={() => handleToggleNotify(true)}
              disabled={notifyBusy}
            >
              On
            </button>
          </div>
        </div>
      )}

      <div className="member-detail-row">
        <span className="field-label">Chat history</span>
        <form
          onSubmit={(e) => { e.preventDefault(); handleSaveRetention(); }}
          className="member-edit-form"
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={retentionInput}
              onChange={(e) => { setRetentionInput(e.target.value); setRetentionError(null); }}
              className="field-input"
              style={{ width: "80px" }}
              aria-label="Days of chat history to keep"
            />
            <span>days</span>
            <button
              type="submit"
              disabled={
                retentionSaving ||
                retentionInput.trim() === "" ||
                Number(retentionInput) === retention
              }
              className="btn-primary"
            >
              {retentionSaving ? "Saving..." : "Save"}
            </button>
          </div>
          <p className="member-detail-hint" style={{ margin: "4px 0 0" }}>
            Messages older than this are deleted for the whole family.
          </p>
          {retentionError && (
            <p className="member-detail-hint" style={{ margin: "4px 0 0", color: "var(--danger, #c00)" }}>
              {retentionError}
            </p>
          )}
        </form>
      </div>
    </>
  );
}
